import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { userRecordResolver } from './services/user-record.resolver';
import { authGroupGuard } from '@k12/admin-services';
import { appRoleGuard, signedOutGuard, studentResolver } from '@k12/app-services';
import { SignOutComponent } from '@k12/app-components';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';

const WORK_QUEUE_BREADCRUMB = 'Work Queue';
const ADMIN_GLOBAL_ADMIN_GROUP = 'K12-admin-Global-admin';
const DOCUMENTS_BREADCRUMB = 'Documents';
const MESSAGES_BREADCRUMB = 'Messages';
const INSTITUTION_RECORD_BREADCRUMB = 'Institution Record';
const INSTITUTION_EDIT_PATH = 'institution/:institutionId/edit';
const COMMUNICATIONS_CENTER_PATH = 'communications-center';
const COMMUNICATIONS_CENTER_BREADCRUMB = 'Emails';
const STUDENT_RECORD_PATH = 'student/:studentId';

// Lazy load helper for message center component
const loadMessageCenterComponent = () =>
  import('./pages/message_center/message_center.component').then((m) => m.MessageCenterComponent);

export const routes: Routes = [
  // Sibling of the guarded tree, not a child: appRoleGuard redirects here, so a guarded
  // /unauthorized would re-run the guard and redirect to itself forever.
  {
    path: 'unauthorized',
    component: UnauthorizedComponent
  },
  {
    // Public: Azure AD returns to the registered origin, then signedOutGuard lands here.
    path: 'sign-out',
    component: SignOutComponent
  },
  {
    path: '',
    canActivate: [signedOutGuard, MsalGuard, appRoleGuard(['K12.Admin'])],
    children: [
      {
        path: 'profile',
        loadComponent: () => import('./profile/profile.component').then((m) => m.ProfileComponent)
      },
      {
        path: 'applications',
        data: { breadcrumb: 'Applications' },
        loadComponent: () => import('./pages/applications/applications.component').then((m) => m.ApplicationsComponent)
      },
      {
        path: 'login',
        data: { breadcrumb: 'Login' },
        loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent)
      },
      {
        path: 'program/:programId',
        data: { breadcrumb: 'Program' },
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () => import('./pages/program/program.component').then((m) => m.ProgramComponent)
      },
      {
        path: 'program-preview',
        data: { breadcrumb: 'Program Preview' },
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () =>
          import('./pages/program-preview/program-preview.component').then((m) => m.ProgramPreviewComponent)
      },
      {
        path: 'create-edit-prompt',
        data: { breadcrumb: 'Create/Edit Prompt' },
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () =>
          import('./pages/create-edit-prompt/create-edit-prompt.component').then((m) => m.CreateEditPromptComponent)
      },
      {
        path: 'home',
        data: { breadcrumb: 'Home' },
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent)
      },
      {
        path: 'view-programs',
        data: { breadcrumb: 'View Programs' },
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () =>
          import('./pages/view-programs/view-programs.component').then((m) => m.ViewProgramsComponent)
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/users/users.component').then((m) => m.UsersComponent)
      },
      {
        path: 'tasks',
        data: { breadcrumb: 'Tasks' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/tasks/tasks-view/tasks-view.component').then((m) => m.TasksViewComponent)
          },
          {
            path: 'templates',
            data: { breadcrumb: 'Task Names' },
            canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/tasks/types/task-types.component').then((m) => m.TaskTypesComponent)
              },
              {
                path: ':id',
                data: { breadcrumb: 'Create Task Name' },
                loadComponent: () => import('./pages/tasks/types/task-type.component').then((m) => m.TaskTypeComponent)
              },
              {
                path: 'update/:id',
                data: { breadcrumb: 'Update Task Name' },
                loadComponent: () => import('./pages/tasks/types/task-type.component').then((m) => m.TaskTypeComponent)
              }
            ]
          },
          {
            path: 'create',
            data: { breadcrumb: 'Assign a New Task' },
            loadComponent: () => import('./pages/tasks/task/task.component').then((m) => m.TaskComponent)
          },
          {
            path: 'update',
            data: { breadcrumb: 'Update Task' },
            loadComponent: () =>
              import('./pages/tasks/edit-form/edit-task-form.component').then((m) => m.EditTaskFormComponent)
          },
          {
            path: ':id',
            data: { breadcrumb: 'User Record', breadcrumbQueryParams: true },
            resolve: { userRecordData: userRecordResolver },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/user-record/admin-user-record.component').then((m) => m.AdminUserRecordComponent)
              },
              {
                path: 'edit',
                data: { breadcrumb: 'Edit User' },
                loadComponent: () =>
                  import('./pages/user-record/admin-user-record-edit.component').then(
                    (m) => m.AdminUserRecordEditComponent
                  )
              },
              {
                path: INSTITUTION_EDIT_PATH,
                data: { breadcrumb: INSTITUTION_RECORD_BREADCRUMB },
                loadComponent: () =>
                  import('./pages/institution-record/institution-record.component').then(
                    (m) => m.InstitutionRecordComponent
                  )
              },
              {
                path: 'tasks',
                data: { breadcrumb: 'Tasks' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/tasks/tasks-view/tasks-view.component').then((m) => m.TasksViewComponent)
                  },
                  {
                    path: 'create',
                    data: { breadcrumb: 'Create Task' },
                    loadComponent: () =>
                      import('./pages/tasks/form/task-form.component').then((m) => m.TaskFormComponent)
                  }
                ]
              },
              {
                path: 'employees',
                data: { breadcrumb: 'Employees' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/user-record/admin-user-record-view-employees.component').then(
                        (m) => m.AdminUserRecordViewEmployeesComponent
                      )
                  },
                  {
                    path: ':employeeId',
                    data: { breadcrumb: 'Employee Details' },
                    loadComponent: () =>
                      import('./pages/user-record/admin-user-record-view-employee-detail.component').then(
                        (m) => m.AdminUserRecordViewEmployeeDetailComponent
                      )
                  }
                ]
              },
              {
                path: 'documents',
                data: { breadcrumb: DOCUMENTS_BREADCRUMB },
                loadComponent: () => import('./pages/documents/documents.component').then((m) => m.DocumentsComponent)
              },
              {
                path: STUDENT_RECORD_PATH,
                data: { breadcrumb: 'Student Record', isAdmin: true },
                resolve: { student: studentResolver },
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () => import('./pages/student/student.component').then((m) => m.StudentComponent)
              },
              {
                path: 'messages',
                data: { breadcrumb: MESSAGES_BREADCRUMB },
                loadComponent: loadMessageCenterComponent
              },
              {
                path: COMMUNICATIONS_CENTER_PATH,
                data: { breadcrumb: COMMUNICATIONS_CENTER_BREADCRUMB },
                loadComponent: () =>
                  import('./pages/communications-center/communications-center.component').then(
                    (m) => m.CommunicationsCenterComponent
                  )
              }
            ]
          }
        ]
      },
      {
        path: 'messages',
        data: { breadcrumb: MESSAGES_BREADCRUMB },
        children: [
          {
            path: '',
            loadComponent: loadMessageCenterComponent
          },
          {
            path: 'auto-assignment',
            data: { breadcrumb: 'Auto-Assignment Rules' },
            canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
            loadComponent: () =>
              import('./pages/message_center/message-autoassignment/message-autoassignment.component').then(
                (m) => m.MessageAutoAssignmentComponent
              )
          }
        ]
      },
      {
        path: 'messages/:id',
        data: { breadcrumb: 'Message Details' },
        loadComponent: loadMessageCenterComponent
      },
      {
        path: 'work-queue',
        data: { breadcrumb: WORK_QUEUE_BREADCRUMB },
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/work-queue/queue.component').then((m) => m.WorkQueueComponent)
          },
          {
            path: 'assignments',
            data: { breadcrumb: 'Auto - Assignment Rules' },
            canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/tasks/auto-assignment-rules/auto-assignment-rules.component').then(
                    (m) => m.AutoAssignmentRulesComponent
                  )
              }
            ]
          },
          {
            path: 'update',
            data: { breadcrumb: 'Update Task' },
            loadComponent: () =>
              import('./pages/tasks/edit-form/edit-task-form.component').then((m) => m.EditTaskFormComponent)
          },
          {
            path: ':id',
            data: { breadcrumb: 'User Record', breadcrumbQueryParams: true },
            resolve: { userRecordData: userRecordResolver },
            runGuardsAndResolvers: 'paramsOrQueryParamsChange',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/user-record/admin-user-record.component').then((m) => m.AdminUserRecordComponent)
              },
              {
                path: 'edit',
                data: { breadcrumb: 'Edit User' },
                loadComponent: () =>
                  import('./pages/user-record/admin-user-record-edit.component').then(
                    (m) => m.AdminUserRecordEditComponent
                  )
              },
              {
                path: INSTITUTION_EDIT_PATH,
                data: { breadcrumb: INSTITUTION_RECORD_BREADCRUMB },
                loadComponent: () =>
                  import('./pages/institution-record/institution-record.component').then(
                    (m) => m.InstitutionRecordComponent
                  )
              },
              {
                path: 'tasks',
                resolve: { userRecordData: userRecordResolver },
                data: { breadcrumb: 'Tasks' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/tasks/tasks-view/tasks-view.component').then((m) => m.TasksViewComponent)
                  },
                  {
                    path: 'create',
                    data: { breadcrumb: 'Create Task' },
                    loadComponent: () =>
                      import('./pages/tasks/form/task-form.component').then((m) => m.TaskFormComponent)
                  }
                ]
              },
              {
                path: 'employees',
                data: { breadcrumb: 'Employees' },
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/user-record/admin-user-record-view-employees.component').then(
                        (m) => m.AdminUserRecordViewEmployeesComponent
                      )
                  },
                  {
                    path: ':employeeId',
                    data: { breadcrumb: 'Employee Details' },
                    loadComponent: () =>
                      import('./pages/user-record/admin-user-record-view-employee-detail.component').then(
                        (m) => m.AdminUserRecordViewEmployeeDetailComponent
                      )
                  }
                ]
              },
              {
                path: 'documents',
                data: { breadcrumb: DOCUMENTS_BREADCRUMB },
                loadComponent: () => import('./pages/documents/documents.component').then((m) => m.DocumentsComponent)
              },
              {
                path: STUDENT_RECORD_PATH,
                data: { breadcrumb: 'Student Record', isAdmin: true },
                resolve: { student: studentResolver },
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () => import('./pages/student/student.component').then((m) => m.StudentComponent)
              },
              {
                path: 'messages',
                data: { breadcrumb: MESSAGES_BREADCRUMB },
                loadComponent: loadMessageCenterComponent
              },
              {
                path: COMMUNICATIONS_CENTER_PATH,
                data: { breadcrumb: COMMUNICATIONS_CENTER_BREADCRUMB },
                loadComponent: () =>
                  import('./pages/communications-center/communications-center.component').then(
                    (m) => m.CommunicationsCenterComponent
                  )
              }
            ]
          }
        ]
      },
      {
        path: 'award-management',
        data: { breadcrumb: 'Award Management' },
        loadComponent: () =>
          import('./pages/award-management/award-management.component').then((m) => m.AwardManagementComponent)
      },
      {
        path: 'award-simulator',
        data: { breadcrumb: 'Award Simulator' },
        loadComponent: () =>
          import('./pages/award-simulator/award-simulator.component').then((m) => m.AwardSimulatorComponent)
      },
      {
        path: 'award-history',
        data: { breadcrumb: 'Award History' },
        loadComponent: () =>
          import('./pages/award-history/award-history.component').then((m) => m.AwardHistoryComponent)
      },
      {
        path: 'dat-files-history',
        data: { breadcrumb: 'DAT files history' },
        loadComponent: () =>
          import('./pages/dat-files-history/dat-files-history.component').then((m) => m.DatFilesHistoryComponent)
      },
      {
        path: 'classwallet-files',
        data: { breadcrumb: 'Class Wallet Files' },
        loadComponent: () =>
          import('./pages/classwallet-files/classwallet-files.component').then((m) => m.ClassWalletFilesComponent)
      },
      {
        path: 'institutions',
        data: { breadcrumb: 'Institutions' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/institutions/institutions.component').then((m) => m.InstitutionsComponent)
          },
          {
            path: 'create',
            data: { breadcrumb: 'Create Institution' },
            loadComponent: () =>
              import('./pages/institutions/create-institution/create-institution.component').then(
                (m) => m.CreateInstitutionComponent
              )
          },
          {
            path: INSTITUTION_EDIT_PATH,
            data: { breadcrumb: INSTITUTION_RECORD_BREADCRUMB },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/institution-record/institution-record.component').then(
                    (m) => m.InstitutionRecordComponent
                  )
              },
              {
                path: 'school/:id',
                data: { breadcrumb: 'School Record', breadcrumbQueryParams: true },
                resolve: { userRecordData: userRecordResolver },
                runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                loadComponent: () =>
                  import('./pages/user-record/admin-user-record.component').then((m) => m.AdminUserRecordComponent)
              }
            ]
          }
        ]
      },
      {
        path: COMMUNICATIONS_CENTER_PATH,
        data: { breadcrumb: 'Communication Center' },
        children: [
          {
            path: '',
            data: { breadcrumb: COMMUNICATIONS_CENTER_BREADCRUMB },
            loadComponent: () =>
              import('./pages/communications-center/communications-center.component').then(
                (m) => m.CommunicationsCenterComponent
              )
          },
          {
            path: 'communications',
            children: [
              {
                path: '',
                data: { breadcrumb: COMMUNICATIONS_CENTER_BREADCRUMB },
                loadComponent: () =>
                  import('./pages/communications-center/communications-center.component').then(
                    (m) => m.CommunicationsCenterComponent
                  )
              }
            ]
          },
          {
            path: 'templates',
            data: { breadcrumb: 'Communication Templates' },
            canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/communications-center/templates/communications-center-template.component').then(
                    (m) => m.CommunicationsCenterTemplateComponent
                  )
              },
              {
                path: 'create',
                loadComponent: () =>
                  import(
                    './pages/communications-center/templates/create-template/communications-center-create-template.component'
                  ).then((m) => m.CommunicationsCenterCreateTemplateComponent),
                data: { breadcrumb: 'Create Communication Template' }
              }
            ]
          },
          {
            path: 'schedule-template',
            data: { breadcrumb: 'Schedule Template' },
            loadComponent: () =>
              import('./pages/communications-center/schedule-template/schedule-template.component').then(
                (m) => m.ScheduleTemplateComponent
              )
          }
        ]
      },
      {
        path: 'unassigned',
        loadComponent: () => import('./pages/unassigned/unassigned.component').then((m) => m.UnassignedComponent)
      },
      {
        path: 'program-settings',
        data: { breadcrumb: 'Program Settings' },
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () =>
          import('./pages/program-settings/program-settings.component').then((m) => m.ProgramSettingsComponent)
      },
      {
        path: 'lottery',
        data: { breadcrumb: 'Run Lottery' },
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () => import('./pages/lottery/lottery.component').then((m) => m.LotteryComponent)
      },
      {
        path: 'recipient-list',
        data: { breadcrumb: 'Recipient List' },
        loadComponent: () =>
          import('./pages/recipient-list/recipient-list.component').then((m) => m.RecipientListComponent)
      },
      {
        path: 'merge-fields',
        data: { breadcrumb: 'Merge Fields' },
        loadComponent: () => import('./pages/merge-fields/merge-fields.component').then((m) => m.MergeFieldsComponent)
      },
      {
        path: 'send-agreement/:templateId',
        data: { breadcrumb: 'Send Agreement' },
        loadComponent: () =>
          import('./pages/send-agreement/send-agreement.component').then((m) => m.SendAgreementComponent)
      },
      {
        path: STUDENT_RECORD_PATH,
        data: { breadcrumb: 'Student', isAdmin: true },
        resolve: { student: studentResolver },
        runGuardsAndResolvers: (from, to) => {
          return (
            from.params['studentId'] !== to.params['studentId'] ||
            from.queryParams['academicYearId'] !== to.queryParams['academicYearId'] ||
            from.children.some((child) => child.url.length > 0)
          );
        },
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/student/student.component').then((m) => m.StudentComponent)
          },
          {
            path: 'osedit',
            data: { breadcrumb: 'Edit Student OS' },
            loadComponent: () => import('@k12/app-components').then((m) => m.StudentOsDetailEditComponent)
          },
          {
            path: 'esaedit',
            data: { breadcrumb: 'Edit Student ESA' },
            loadComponent: () => import('@k12/app-components').then((m) => m.StudentEsaDetailEditComponent)
          },
          {
            path: 'edit',
            data: { breadcrumb: 'Edit Student Record' },
            loadComponent: () => import('@k12/app-components').then((m) => m.StudentInformationEditComponent)
          },
          {
            path: ':programType/application/print',
            data: { breadcrumb: 'Print Scholarship Application' },
            loadComponent: () =>
              import('./pages/scholarship-application-print/scholarship-application-print.component').then(
                (m) => m.ScholarshipApplicationPrintComponent
              )
          }
        ]
      },
      {
        path: 'rosters',
        loadComponent: () => import('./pages/rosters/rosters.component').then((m) => m.RostersComponent)
      },
      {
        path: 'dnpe-import',
        canActivate: [authGroupGuard(ADMIN_GLOBAL_ADMIN_GROUP)],
        loadComponent: () => import('./pages/dnpe-import/dnpe-import.component').then((m) => m.DnpeImportComponent)
      },
      {
        path: '*',
        data: { breadcrumb: WORK_QUEUE_BREADCRUMB },
        loadComponent: () => import('./pages/work-queue/queue.component').then((m) => m.WorkQueueComponent)
      },
      {
        path: '**',
        data: { breadcrumb: WORK_QUEUE_BREADCRUMB },
        loadComponent: () => import('./pages/work-queue/queue.component').then((m) => m.WorkQueueComponent)
      }
    ]
  }
];

